import {
    AlignmentType,
    BorderStyle,
    convertInchesToTwip,
    Document,
    ExternalHyperlink,
    HeadingLevel,
    LevelFormat,
    Math,
    MathFraction,
    MathRadical,
    MathRun,
    MathSubScript,
    MathSubSuperScript,
    MathSuperScript,
    Paragraph,
    Table,
    TableCell,
    TableLayoutType,
    TableRow,
    TextRun,
    VerticalAlign,
    WidthType,
    type ILevelParagraphStylePropertiesOptions,
} from "docx";
import {
    Element,
    Node,
    type Descendant,
    type Editor,
    type TableElement,
    type Text,
} from "slate";

import {convertToAbsoluteUrl} from "@khan/component-markdown/rules";
import {render} from "@khan/component-tex";

import {getDocumentTitle} from "./slate/helpers/editor-helpers.ts";

interface recursiveState {
    target: "google-drive" | "ms-onedrive" | "docx-download";
    listInstanceCount: number;
    listStack: {
        kind: "bulleted-list" | "numbered-list" | "lettered-list";
        instance: number;
        length: number;
        currentIndex: number;
        hasNestedList?: boolean;
    }[];
    textStyleName?: string;
}

const paragraphSpacingAfter = 330; // 22px * 15 twip/px

/**
 * createDocFromEditor takes a Slate editor and returns a docx Document
 * object. Slate has three main node types: Editor, Element, and Text. This
 * roughly maps to the docx Document, Paragraph, and TextRun objects.
 */
export function createDocFromEditor(
    editor: Editor,
    target: "google-drive" | "ms-onedrive" | "docx-download",
): Document {
    const state: recursiveState = {
        target,
        listInstanceCount: 0,
        listStack: [],
    };

    const sectionChildren = editorToDocxNodes(editor, state);

    const isGoogleDoc = target === "google-drive";
    const baseFontSize = 24;

    const listStyleForDepth = (
        depth: number,
    ): ILevelParagraphStylePropertiesOptions => ({
        indent: {
            left: convertInchesToTwip(0.5 * depth),
            hanging: convertInchesToTwip(0.25),
        },
    });

    return new Document({
        title: getDocumentTitle(editor),
        sections: [
            {
                properties: {
                    page: {
                        margin: {
                            top: convertInchesToTwip(0.5),
                            bottom: convertInchesToTwip(0.5),
                            left: convertInchesToTwip(0.5),
                            right: convertInchesToTwip(0.5),
                        },
                    },
                },
                children: sectionChildren,
            },
        ],
        styles: {
            default: {
                document: {
                    run: {
                        font: isGoogleDoc ? "Lato" : "Arial",
                        color: "#23242B",
                        size: baseFontSize,
                    },
                    paragraph: {
                        spacing: {
                            after: paragraphSpacingAfter,
                        },
                    },
                },
                heading1: {
                    run: {
                        font: isGoogleDoc
                            ? "Source Serif Pro"
                            : "Times New Roman",
                        color: "#444444",
                        size: window.Math.round(baseFontSize * 1.5),
                    },
                },
                heading2: {
                    run: {
                        bold: true,
                        size: window.Math.round(baseFontSize * 1.3),
                    },
                },
                heading3: {
                    run: {
                        bold: true,
                        size: window.Math.round(baseFontSize * 1.1),
                    },
                },
                hyperlink: {
                    run: {
                        color: "#1865F2",
                        underline: {
                            color: "#1865F2",
                        },
                    },
                },
            },
        },
        numbering: {
            config: [
                {
                    reference: "numbered-list",
                    levels: Array.from({length: 9}, (_, i) => {
                        // 9 levels, starting at 0
                        return {
                            level: i,
                            format: LevelFormat.DECIMAL,
                            text: `%${i + 1}.`,
                            alignment: AlignmentType.START,
                            style: {
                                paragraph: listStyleForDepth(i),
                            },
                        };
                    }),
                },
                {
                    reference: "lettered-list",
                    levels: Array.from({length: 9}, (_, i) => {
                        // 9 levels, starting at 0
                        return {
                            level: i,
                            format: LevelFormat.LOWER_LETTER,
                            text: `%${i + 1}.`,
                            alignment: AlignmentType.START,
                            style: {
                                paragraph: listStyleForDepth(i),
                            },
                        };
                    }),
                },
                {
                    reference: "bulleted-list",
                    levels: Array.from({length: 9}, (_, i) => {
                        // 9 levels, starting at 0
                        return {
                            level: i,
                            format: LevelFormat.BULLET,
                            text: "\u2022",
                            alignment: AlignmentType.START,
                            style: {
                                paragraph: listStyleForDepth(i),
                            },
                        };
                    }),
                },
            ],
        },
    });
}

const editorToDocxNodes = (
    editor: Editor,
    state: recursiveState,
): (Paragraph | Table)[] => {
    const sectionChildren: (Paragraph | Table)[] = [];

    for (const topLevelElement of editor.children) {
        if (Element.isElement(topLevelElement)) {
            if (topLevelElement.type === "table") {
                // Tables can only appear at the top level of an editor, and
                // they require special processing for their rows/cells, so we
                // handle it as a special case here:
                sectionChildren.push(
                    tableElementToDocxNode(topLevelElement, state),
                );
                continue;
            }

            sectionChildren.push(
                ...createParagraphsFromElement(topLevelElement, state),
            );
        }
    }

    return sectionChildren;
};

const tableElementToDocxNode = (
    table: TableElement,
    state: recursiveState,
): Table => {
    const rows: TableRow[] = [];

    let columnCount = 1;

    for (const headerOrBodyElement of table.children) {
        const isHeader = headerOrBodyElement.type === "table-header";

        for (const rowElement of headerOrBodyElement.children) {
            const cells: TableCell[] = [];

            for (const cellElement of rowElement.children) {
                cells.push(
                    new TableCell({
                        verticalAlign: VerticalAlign.CENTER,
                        children: createParagraphsFromElement(
                            cellElement,
                            state,
                        ),
                        margins: {
                            top: convertInchesToTwip(0.125),
                            bottom: convertInchesToTwip(0.125),
                            left: convertInchesToTwip(0.125),
                            right: convertInchesToTwip(0.125),
                        },
                        shading: isHeader
                            ? {fill: "eeeeee", color: "auto"}
                            : undefined,
                    }),
                );
            }

            columnCount = window.Math.max(columnCount, cells.length);

            rows.push(
                new TableRow({
                    children: cells,
                }),
            );
        }
    }

    const columnWidth = convertInchesToTwip(7.25) / columnCount;

    return new Table({
        layout: TableLayoutType.FIXED,
        width: {size: 100, type: WidthType.PERCENTAGE},
        columnWidths: Array(columnCount).fill(columnWidth),
        rows,
    });
};

// createParagraphsFromElement takes a Slate element node and returns docx
// Paragraphs. Slate element nodes can contain other Slate element or text
// nodes, so we need to recursively traverse the Slate element node's children
// to create docx Paragraph or TextRun.
//
// Since list-items are single Slate elements but may contain nested children,
// these are "flattened" here - all list-items are moved to top-level docx
// nodes.
const createParagraphsFromElement = (
    element: Element,
    state: recursiveState,
): Paragraph[] => {
    // Create a docx Paragraph from the Slate element node depending
    // on the Slate element node's type.
    switch (element.type) {
        case "heading-one":
            return [
                new Paragraph({
                    children: childrenToDocxNodes(element.children, state),
                    heading: HeadingLevel.HEADING_1,
                }),
            ];

        case "heading-two":
            return [
                new Paragraph({
                    children: childrenToDocxNodes(element.children, state),
                    heading: HeadingLevel.HEADING_2,
                }),
            ];

        case "heading-three":
            return [
                new Paragraph({
                    children: childrenToDocxNodes(element.children, state),
                    heading: HeadingLevel.HEADING_3,
                }),
            ];

        case "block-quote":
            return [
                new Paragraph({
                    children: childrenToDocxNodes(element.children, state),
                    border: {
                        left: {
                            color: "#dbdbdd", // rgba(33, 36, 44, 0.16) in hex
                            size: 12, // 2px * 0.75 pt/px * 8
                            space: 6, // 8 px * 0.75 pt/px
                            style: BorderStyle.SINGLE,
                        },
                    },
                }),
            ];

        case "numbered-list":
        case "lettered-list":
        case "bulleted-list":
            // Since lists can be nested in complex ways, we keep track of a
            // stack of details representing the list nesting. The last element
            // of this array is the deepest list we're currently inside of.
            state.listStack.push({
                kind: element.type,
                instance: state.listInstanceCount,
                length: element.children.length,
                currentIndex: 0,
            });

            // We also need each list in the document to have a distinct
            // `instance` value, so that the docx list-numbering system
            // groups the right list items together.
            state.listInstanceCount++;

            const itemParagraphs: Paragraph[] = [];

            for (let i = 0; i < element.children.length; i++) {
                const item = element.children[i];

                state.listStack.at(-1)!.currentIndex = i;
                state.listStack.at(-1)!.hasNestedList =
                    item.children.length > 1;

                itemParagraphs.push(
                    ...createParagraphsFromElement(item, state),
                );
            }

            state.listStack.pop();

            return itemParagraphs;

        case "list-item":
            // list-items are just wrappers around two kinds of element:
            // list-item-texts (which are handled below, and which contain all
            // the actual item text content and formatting), and a nested list,
            // if there is one. Here, we just return either the processed
            // list item (the first child), or the list item followed by the
            // processed nested list (the second child).

            const itemNodes = createParagraphsFromElement(
                element.children[0],
                state,
            );

            if (element.children.length === 1) {
                return itemNodes;
            }

            const nestedListNodes = createParagraphsFromElement(
                element.children[1],
                state,
            );

            return [...itemNodes, ...nestedListNodes];

        case "list-item-text":
            const depth = state.listStack.length;
            const {kind, length, currentIndex, hasNestedList, instance} =
                state.listStack.at(-1)!;
            const isLastItem = currentIndex === length - 1 && !hasNestedList;

            return [
                new Paragraph({
                    children: childrenToDocxNodes(element.children, state),
                    numbering: {
                        reference: kind,
                        level: depth,
                        instance,
                    },
                    spacing: {
                        after: isLastItem
                            ? paragraphSpacingAfter
                            : paragraphSpacingAfter / 2,
                    },
                }),
            ];

        case "math-block":
            return [
                new Paragraph({
                    children: [createMathFromTex(element.tex)],
                }),
            ];

        case "code-block":
            const text = element.children.map((c) => Node.string(c)).join("");
            const lines = text.split("\n");
            const font =
                state.target === "google-drive"
                    ? "Source Code Pro"
                    : "Courier New";

            // To preserve line breaks in the code, we split into lines and
            // make each line its own paragraph:
            return lines.map(
                (text, i) =>
                    new Paragraph({
                        children: [
                            new TextRun({
                                text,
                                shading: {fill: "eeeeee"},
                                font,
                            }),
                        ],
                        spacing: {
                            afterAutoSpacing: true,
                            after:
                                i === lines.length - 1
                                    ? paragraphSpacingAfter
                                    : 0,
                        },
                    }),
            );

        case "horizontal-rule":
            return [
                new Paragraph({
                    children: [],
                    border: {
                        bottom: {
                            size: 8,
                            space: 0,
                            style: BorderStyle.SINGLE,
                        },
                    },
                    spacing: {
                        after: paragraphSpacingAfter,
                    },
                }),
            ];

        case "table":
        case "table-header":
        case "table-body":
        case "table-row":
        case "table-cell":
            // NOTE: since these elements appear within tables, they should
            // be handled by the special case above. But just in case something
            // strange happens, we fall back to rendering them as plain
            // paragraphs here - better than leaving them out entirely.
            return [
                new Paragraph({
                    children: childrenToDocxNodes(element.children, state),
                }),
            ];

        case "math-editor":
        case "math-inline":
        case "code-inline":
        case "link":
            // NOTE: since these are inline elements, they should really be
            // handled by `childrenToDocxNodes` below, as children of their parent
            // paragraph. But just in case something strange happens, we fall back
            // to rendering them as the default (plain paragraphs) here - better
            // than leaving them out of the doc entirely.
            return [
                new Paragraph({
                    children: childrenToDocxNodes(element.children, state),
                }),
            ];

        case "paragraph":
        default:
            return [
                new Paragraph({
                    children: childrenToDocxNodes(element.children, state),
                }),
            ];
    }
};

const childrenToDocxNodes = (
    descendants: Descendant[],
    state: recursiveState,
) => {
    const children: (Paragraph | Math | ExternalHyperlink | TextRun)[] = [];

    for (const child of descendants) {
        if (Element.isElement(child)) {
            switch (child.type) {
                case "math-editor":
                case "math-inline":
                    children.push(createMathFromTex(child.tex));
                    break;

                case "code-inline":
                    const text = child.children
                        .map((c) => Node.string(c))
                        .join("");

                    children.push(
                        new TextRun({
                            text,
                            shading: {fill: "eeeeee"},
                            font:
                                state.target === "google-drive"
                                    ? "Source Code Pro"
                                    : "Courier New",
                        }),
                    );
                    break;

                case "link":
                    children.push(
                        new ExternalHyperlink({
                            // We need to make this URL absolute, since hrefs
                            // like "/math/..." won't make sense once exported:
                            link: toAbsoluteUrl(child.url),
                            children: childrenToDocxNodes(child.children, {
                                ...state,
                                textStyleName: "Hyperlink",
                            }),
                        }),
                    );
                    break;

                default:
                    children.push(...createParagraphsFromElement(child, state));
            }
        } else {
            children.push(createTextRunFromText(child, state.textStyleName));
        }
    }

    return children;
};

// createTextRunFromText takes a Slate text node and returns a docx TextRun
// object.
const createTextRunFromText = (text: Text, style?: string): TextRun => {
    const textRun = new TextRun({
        text: text.text,
        bold: text.bold,
        italics: text.italic,
        style: style,
    });

    return textRun;
};

type mathChild =
    | MathSubSuperScript
    | MathSubScript
    | MathSuperScript
    | MathFraction
    | MathRadical
    | MathRun;

// createMathFromTex takes a TeX string and returns a docx Math object.
export const createMathFromTex = (tex: string): Math => {
    const {domElement} = render(tex);
    const mathElement = domElement.querySelector("math");

    if (mathElement == null) {
        return new Math({
            children: [new MathRun(tex)],
        });
    }

    const mathChildren: mathChild[] = [];

    for (const child of mathElement.children) {
        const element = child as MathMLElement;

        mathChildren.push(...createChildrenFromElement(element));
    }

    return new Math({
        children: mathChildren,
    });
};

// createChildrenFromElement takes a MathMLElement and returns an array of
// mathChild objects.
const createChildrenFromElement = (element: MathMLElement): mathChild[] => {
    const mathChildren: mathChild[] = [];

    switch (element.tagName) {
        case "mrow":
        case "mstyle":
        case "mpadded":
            for (const child of element.children) {
                const element = child as MathMLElement;

                mathChildren.push(...createChildrenFromElement(element));
            }

            break;
        case "mn":
        case "mo":
        case "mi":
            if (element.textContent) {
                mathChildren.push(new MathRun(element.textContent));
            }
            break;
        case "msqrt":
            if (element.children.length > 0) {
                mathChildren.push(
                    new MathRadical({
                        children: createChildrenFromElement(
                            element.children[0] as MathMLElement,
                        ),
                    }),
                );
            }
            break;
        case "mfrac":
            if (element.children.length !== 2) {
                break;
            }

            const numeratorElement = element.children[0] as MathMLElement;
            const denominatorElement = element.children[1] as MathMLElement;

            mathChildren.push(
                new MathFraction({
                    numerator: createChildrenFromElement(numeratorElement),
                    denominator: createChildrenFromElement(denominatorElement),
                }),
            );

            break;
        case "mroot":
            if (element.children.length !== 2) {
                break;
            }

            const radicandElement = element.children[0] as MathMLElement;
            const degreeElement = element.children[1] as MathMLElement;

            mathChildren.push(
                new MathRadical({
                    children: createChildrenFromElement(radicandElement),
                    degree: createChildrenFromElement(degreeElement),
                }),
            );

            break;
        case "msubsup":
            if (element.children.length !== 3) {
                break;
            }

            const childElement3 = element.children[0] as MathMLElement;
            const subScriptElement2 = element.children[1] as MathMLElement;
            const superScriptElement2 = element.children[2] as MathMLElement;

            mathChildren.push(
                new MathSubSuperScript({
                    children: createChildrenFromElement(childElement3),
                    subScript: createChildrenFromElement(subScriptElement2),
                    superScript: createChildrenFromElement(superScriptElement2),
                }),
            );

            break;
        case "msub":
            if (element.children.length !== 2) {
                break;
            }

            const childElement = element.children[0] as MathMLElement;
            const subScriptElement = element.children[1] as MathMLElement;

            mathChildren.push(
                new MathSubScript({
                    children: createChildrenFromElement(childElement),
                    subScript: createChildrenFromElement(subScriptElement),
                }),
            );

            break;
        case "msup":
            if (element.children.length !== 2) {
                break;
            }

            const childElement2 = element.children[0] as MathMLElement;
            const superScriptElement = element.children[1] as MathMLElement;

            mathChildren.push(
                new MathSuperScript({
                    children: createChildrenFromElement(childElement2),
                    superScript: createChildrenFromElement(superScriptElement),
                }),
            );

            break;
        case "mphantom":
            // Text is sometimes used for spacing, but we don't want to include
            // it in the math run
            break;
        case "mtable":
            for (const child of element.children) {
                const element = child as MathMLElement;
                mathChildren.push(...createChildrenFromElement(element));
            }
            break;
        default:
            if (element.textContent && element.textContent.trim().length > 0) {
                mathChildren.push(new MathRun(element.textContent));
            }
    }

    return mathChildren;
};

const toAbsoluteUrl = (maybeRelativeUrl: string) => {
    return convertToAbsoluteUrl(maybeRelativeUrl) || maybeRelativeUrl;
};
